// FILE: src/components/TargetedQuizModal.js
//
// Confirmation modal shown before launching a Targeted Quiz.
// Displays the 1–2 weak phases identified from the last session and lets the
// user start the quiz or cancel back to the Results page.
//
// ── Props ─────────────────────────────────────────────────────────────────────
//
//   weakPhases     {PhaseMetric[]}   1–2 weak phase objects from getWeakPhases()
//   examId         {string}          e.g. 'cpmai' — used for dimension label
//   questionCount  {number}          how many questions will be in the quiz
//   onStart        {function}        called with no args when user confirms
//   onClose        {function}        called when user cancels

import React from 'react';

// ── Colour helpers ────────────────────────────────────────────────────────────

function pctColor(pct) {
  if (pct >= 70) return '#2e7d32';
  if (pct >= 50) return '#e07b00';
  return '#c62828';
}

function pctBg(pct) {
  if (pct >= 70) return '#f1f8f1';
  if (pct >= 50) return '#fff3e0';
  return '#fff5f5';
}

// ── Styles ────────────────────────────────────────────────────────────────────

const S = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '16px',
  },
  modal: {
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
    padding: '28px 28px 24px',
    maxWidth: '440px',
    width: '100%',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
  },
  icon: {
    fontSize: '2rem',
    textAlign: 'center',
    marginBottom: '10px',
  },
  heading: {
    fontSize: '1.2rem',
    fontWeight: '700',
    color: '#222',
    margin: '0 0 6px',
    textAlign: 'center',
  },
  subheading: {
    fontSize: '0.88rem',
    color: '#777',
    textAlign: 'center',
    margin: '0 0 20px',
  },
  phaseList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '22px',
  },
  phaseRow: (pct) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 14px',
    borderRadius: '8px',
    background: pctBg(pct),
    border: `1px solid ${pctColor(pct)}30`,
  }),
  phaseInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  phaseLabel: {
    fontWeight: '700',
    fontSize: '0.95rem',
    color: '#333',
  },
  phaseKey: {
    fontSize: '0.78rem',
    color: '#888',
  },
  pctBadge: (pct) => ({
    fontWeight: '800',
    fontSize: '1.1rem',
    color: pctColor(pct),
  }),
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.83rem',
    color: '#888',
    marginBottom: '20px',
    justifyContent: 'center',
  },
  divider: {
    border: 'none',
    borderTop: '1px solid #f0f0f0',
    margin: '0 0 18px',
  },
  buttonRow: {
    display: 'flex',
    gap: '10px',
  },
  cancelButton: {
    flex: 1,
    padding: '10px',
    fontSize: '0.92rem',
    background: 'transparent',
    border: '1px solid #ccc',
    borderRadius: '7px',
    cursor: 'pointer',
    color: '#555',
    fontFamily: 'inherit',
  },
  startButton: {
    flex: 2,
    padding: '10px',
    fontSize: '0.92rem',
    fontWeight: '700',
    background: '#1565c0',
    border: 'none',
    borderRadius: '7px',
    cursor: 'pointer',
    color: '#fff',
    fontFamily: 'inherit',
  },
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function TargetedQuizModal({ weakPhases, examId, questionCount, onStart, onClose }) {
  const dimensionLabel = examId === 'cpmai' ? 'Phase' : 'Domain';

  // Close on overlay click, but not on modal click
  function handleOverlayClick(e) {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <div
      data-testid="targeted-quiz-modal"
      style={S.overlay}
      onClick={handleOverlayClick}
    >
      <div style={S.modal}>
        <div style={S.icon}>🎯</div>
        <h2 style={S.heading}>Targeted Practice Quiz</h2>
        <p style={S.subheading}>
          We recommend focusing on{' '}
          {weakPhases.length === 1
            ? `this ${dimensionLabel.toLowerCase()}`
            : `these ${dimensionLabel.toLowerCase()}s`}{' '}
          based on your last session:
        </p>

        {/* Weak phase cards */}
        <div style={S.phaseList}>
          {weakPhases.map((phase) => (
            <div key={phase.key} style={S.phaseRow(phase.pct)}>
              <div style={S.phaseInfo}>
                <span style={S.phaseLabel}>{phase.label}</span>
                <span style={S.phaseKey}>{phase.key}</span>
              </div>
              <span style={S.pctBadge(phase.pct)}>{phase.pct}%</span>
            </div>
          ))}
        </div>

        {/* Quiz metadata */}
        <div style={S.metaRow}>
          <span>📝</span>
          <span>{questionCount} questions · untimed · practice mode</span>
        </div>

        <hr style={S.divider} />

        {/* Action buttons */}
        <div style={S.buttonRow}>
          <button
            data-testid="targeted-modal-cancel"
            style={S.cancelButton}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            data-testid="targeted-modal-start"
            style={S.startButton}
            onClick={onStart}
          >
            Start Quiz →
          </button>
        </div>
      </div>
    </div>
  );
}
